import {
  Flex,
  Divider,
  Text,
  Box,
  Tooltip,
  Avatar,
  useDisclosure,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ContributorLoadingCell from '../LoadingCell/ContributorLoadingCell';
import { QUERY_KEYS } from '../../graphql';
import { EditPencilIcon, UserMergeAlertIcon } from '../../../../icons';
import { ContributorsAccounts } from '../../../../ContributorAccounts';
import MergeContributorModal from '../../../../MergeContributorModal';
import EditContributorModal from '../../../../EditContributorModal';
import EditEmailModal from '../../../../EditEmailModal';
import { truncateText } from '../../../../shared/utils';
import { Contributor, ContributorAccount, ContributorsList } from '../../types';

type Props = {
  contributors: ContributorsList;
  loading: boolean;
  rowHeight?: string;
};

const StyledLink = styled(Link)`
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.text.secondary2};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  display: block;
`;

const ContributorCol = ({ contributors, loading, rowHeight }: Props) => {
  const mergeIndividualContributorModal = useDisclosure();
  const editContributorRoleModal = useDisclosure();
  const editEmailModal = useDisclosure();

  const [contributorData, setContributorData] = useState<Contributor | null>(
    null
  );
  const queryClient = useQueryClient();

  const openModalWithData = (data: Contributor) => {
    mergeIndividualContributorModal.onOpen();
    setContributorData(data);
  };
  return (
    <Flex
      direction="column"
      position="sticky"
      left={0}
      bg="white"
      h={'fit-content'}
      minW="340px"
      zIndex={89}
    >
      <Flex
        bg="#FBFBFB"
        position="sticky"
        top={0}
        align="center"
        minH="52px"
        w="full"
        px={4}
        zIndex={89}
      >
        <Flex align="center">
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary2"
            w="340px"
          >
            Contributors
          </Text>
        </Flex>
      </Flex>

      <Divider />

      {loading
        ? [...Array(10)].map((_, index) => (
            <ContributorLoadingCell key={index} rowHeight={rowHeight} />
          ))
        : contributors?.map((row: Contributor, index: number) => (
            <Fragment key={`row-${index}`}>
              <Flex
                direction="column"
                align="flex-start"
                minH={rowHeight || '110px'}
                w="full"
                p={3}
                pl={4}
                bg="white"
              >
                <Flex w="full" align="flex-start" gap={2}>
                  <Avatar size="xs" name={row.name} />

                  <Flex w="full" direction="column" gap={2}>
                    <Flex>
                      <Flex direction="column" gap={1}>
                        <Flex gap={1} mb={1} alignItems={'center'}>
                          {row.name && row.name !== ' ' ? (
                            <Tooltip
                              label={row.name}
                              hasArrow
                              isDisabled={row.name.length < 26}
                            >
                              <StyledLink to={`/contributors/${row.email}`}>
                                <Text
                                  fontSize="sm"
                                  fontFamily={'heading'}
                                  color="text.primary"
                                  fontWeight="medium"
                                >
                                  {truncateText(row.name, 26)}
                                </Text>
                              </StyledLink>
                            </Tooltip>
                          ) : (
                            <Text
                              fontSize="sm"
                              fontFamily={'heading'}
                              color="text.secondary"
                              fontWeight="normal"
                            >
                              Name Not Assigned
                            </Text>
                          )}
                          <Tooltip label="Edit Contributor Name and Job Role">
                            <Box
                              onClick={() => {
                                setContributorData(row);
                                editContributorRoleModal.onOpen();
                              }}
                              cursor={'pointer'}
                            >
                              <EditPencilIcon />
                            </Box>
                          </Tooltip>
                        </Flex>

                        {row.jobRole ? (
                          <Tooltip
                            label={row.jobRole}
                            hasArrow
                            isDisabled={row.jobRole.length < 27}
                          >
                            <Text
                              fontSize="sm"
                              fontFamily={'heading'}
                              color="text.secondary2"
                              fontWeight="normal"
                            >
                              {truncateText(row.jobRole, 27)}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text
                            fontSize="sm"
                            fontFamily={'heading'}
                            color="text.secondary"
                            fontWeight="normal"
                          >
                            Job Role Not Assigned
                          </Text>
                        )}
                      </Flex>
                      {row?.suggestedEmail &&
                        !(
                          row?.suggestedEmail === 'NULL' ||
                          row?.suggestedEmail === 'null'
                        ) && (
                          <Box ml={'auto'}>
                            <Tooltip label="Merge Contributor" hasArrow>
                              <Box
                                mt={1}
                                onClick={() => openModalWithData(row)}
                                cursor={'pointer'}
                              >
                                <UserMergeAlertIcon />
                              </Box>
                            </Tooltip>
                          </Box>
                        )}
                    </Flex>
                    <Flex w="full" justify={'space-between'} align={'center'}>
                      <Flex align="center" gap={1}>
                        <Tooltip
                          label={row.email}
                          hasArrow
                          isDisabled={row.email.length < 27}
                        >
                          <StyledLink to={`/contributors/${row.email}`}>
                            {truncateText(row.email, 27)}
                          </StyledLink>
                        </Tooltip>
                        {!row?.isOfficialEmailPresent && (
                          <Tooltip label="Edit Contributor Official Email Address">
                            <Box
                              onClick={() => {
                                setContributorData(row);
                                editEmailModal.onOpen();
                              }}
                              cursor={'pointer'}
                            >
                              <EditPencilIcon />
                            </Box>
                          </Tooltip>
                        )}
                      </Flex>

                      <ContributorsAccounts
                        accounts={
                          row?.contributorAccountList?.map(
                            (el: ContributorAccount) => ({
                              type: el.key.type,
                              accountId: el.key.account,
                            })
                          ) || []
                        }
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <Divider />
            </Fragment>
          ))}
      {mergeIndividualContributorModal.isOpen && (
        <MergeContributorModal
          isOpen={mergeIndividualContributorModal.isOpen}
          onClose={mergeIndividualContributorModal.onClose}
          data={contributorData}
          successAction={() => {
            mergeIndividualContributorModal.onClose();
            queryClient.invalidateQueries([QUERY_KEYS.contributorList]);
            queryClient.invalidateQueries([QUERY_KEYS.contributorTable]);
          }}
        />
      )}
      {editContributorRoleModal.isOpen && (
        <EditContributorModal
          isOpen={editContributorRoleModal.isOpen}
          onClose={editContributorRoleModal.onClose}
          contributorEmail={contributorData?.email}
          contributorFirstName={contributorData?.name?.split(' ')[0]}
          contributorLastName={contributorData?.name
            ?.split(' ')
            .slice(1)
            .join(' ')}
          contributorRole={contributorData?.jobRole}
        />
      )}

      {editEmailModal.isOpen && (
        <EditEmailModal
          isOpen={editEmailModal.isOpen}
          onClose={editEmailModal.onClose}
          contributorData={contributorData}
          queryKeys={QUERY_KEYS}
        />
      )}
    </Flex>
  );
};

export default ContributorCol;
